import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { oneKlicks } from '../../SDK/oneclick';
import ImageShow from "../../Uitiles/ImageShow";
import formatDoctorName from '../../Uitiles/FormatName';

const AddAppointmentModal = ({ showAdd, setShowAdd, onSuccess }) => {
    const handleClose = () => {
        setShowAdd(false);
        // Reset all form fields
        setTimeout(() => {
            setLocation('');
            setDoctor('');
            setHospital('');
            setSelectedHospital({});
            setSelectedCoords(null);
            setPatient({});
            setPatientSearchTerm('');
            setAppointmentDate('');
            setSelectedTimeSlot('');
            setBookingNotes('');
            setBookingStatus('');
            setMessage('');
            setAppointmentSettings([]);
            setAvailableTimeSlots([]);
        }, 300);
    };
    const [location, setLocation] = useState('');
    const [doctor, setDoctor] = useState('');
    const [locationSuggestions, setLocationSuggestions] = useState([]);
    const [isSearchingLocation, setIsSearchingLocation] = useState(false);
    const [selectedCoords, setSelectedCoords] = useState(null);
    const [hospital, setHospital] = useState('');
    const [hospitalList, setHospitalList] = useState([]);
    const [appointmentSettings, setAppointmentSettings] = useState([]);
    const [message, setMessage] = useState('');
    const [selectedHospital, setSelectedHospital] = useState({});
    const [isSearchingHospitals, setIsSearchingHospitals] = useState(false);
    const [doctorList, setDoctorList] = useState([]);
    const [appointmentDate, setAppointmentDate] = useState('');
    const [calendarMonth, setCalendarMonth] = useState(() => {
        const d = new Date();
        return new Date(d.getFullYear(), d.getMonth(), 1);
    });
    const [patient, setPatient] = useState({});
    const [patientList, setPatientList] = useState([]);
    const [patientSearchTerm, setPatientSearchTerm] = useState('');
    const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
    const [selectedTimeSlot, setSelectedTimeSlot] = useState('');
    const [isLoadingSlots, setIsLoadingSlots] = useState(false);
    const [bookingStatus, setBookingStatus] = useState('');
    const [bookingNotes, setBookingNotes] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const formatLocalDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Generate time slots from appointment settings
    const generateTimeSlots = (settings) => {
        if (!settings || settings.length === 0) return [];

        const setting = settings[0]; // Use first setting
        const slots = [];

        try {
            // Check if using day_time_slots (day-based) or start_time/end_time (general)
            if (setting.day_time_slots) {
                const dayTimeSlots = JSON.parse(setting.day_time_slots);
                const selectedDays = JSON.parse(setting.selected_days || setting.week_days || '[]');

                // Get day of week for the selected date
                const dateObj = new Date(appointmentDate);
                const dayIndex = dateObj.getDay(); // 0 = Sunday, 1 = Monday, etc.
                const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
                const dayName = dayNames[dayIndex];

                // Check if the doctor is available on this day
                if (!selectedDays.includes(dayName)) {
                    return [];
                }

                // Get time slots for this specific day
                const daySlots = dayTimeSlots[dayName];
                if (daySlots && Array.isArray(daySlots)) {
                    daySlots.forEach(slot => {
                        if (slot.start_time && slot.end_time) {
                            const slotTimes = generateSlotsFromRange(
                                slot.start_time,
                                slot.end_time,
                                parseInt(setting.durations) || 30,
                                parseInt(setting.intervals) || 0
                            );
                            slots.push(...slotTimes);
                        }
                    });
                }
            } else if (setting.start_time && setting.end_time) {
                // Old format with single start_time and end_time
                const slotTimes = generateSlotsFromRange(
                    setting.start_time,
                    setting.end_time,
                    parseInt(setting.durations) || 30,
                    parseInt(setting.intervals) || 0
                );
                slots.push(...slotTimes);
            }
        } catch (error) {
            console.error('Error parsing appointment settings:', error);
        }

        return slots;
    };

    // Generate individual time slots from a time range
    const generateSlotsFromRange = (startTime, endTime, duration, interval) => {
        const slots = [];
        const [startHour, startMinute] = startTime.split(':').map(Number);
        const [endHour, endMinute] = endTime.split(':').map(Number);

        let currentMinutes = startHour * 60 + startMinute;
        const endMinutes = endHour * 60 + endMinute;

        while (currentMinutes + duration <= endMinutes) {
            const hour = Math.floor(currentMinutes / 60);
            const minute = currentMinutes % 60;
            const timeString = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
            slots.push(timeString);
            currentMinutes += duration + interval;
        }

        return slots;
    };

    // Check booked appointments for the selected date
    const checkBookedAppointments = async (date, doctorId, hospitalId) => {
        try {
            const tableSchema = '';
            const tableName = 'appointments';
            const conditions = {
                doctor_id: doctorId,
                hospital_id: hospitalId,
                appointment_date: date,
            };
            const orderBy = 'appointment_time ASC';
            const search = {};
            const pagination = {};
            const margedata = [];

            const response = await oneKlicks.read(tableSchema, tableName, conditions, orderBy, search, pagination, margedata);

            if (response.success === true && response.data) {
                // Return array of booked time slots
                return response.data.map(booking => booking.appointment_time);
            }
            return [];
        } catch (error) {
            console.error('Error checking booked appointments:', error);
            return [];
        }
    };

    // Load available time slots when date, doctor, or hospital changes
    useEffect(() => {
        const loadAvailableSlots = async () => {
            if (!appointmentDate || !doctor.id || !selectedHospital.id || appointmentSettings.length === 0) {
                setAvailableTimeSlots([]);
                return;
            }

            setIsLoadingSlots(true);
            try {
                // Generate all possible slots
                const allSlots = generateTimeSlots(appointmentSettings);

                // Get booked slots
                const bookedSlots = await checkBookedAppointments(appointmentDate, doctor.id, selectedHospital.id);

                // Get max spaces per slot
                const spaces = parseInt(appointmentSettings[0]?.spaces) || 1;

                // Count how many times each slot is booked
                const slotCounts = {};
                bookedSlots.forEach(slot => {
                    slotCounts[slot] = (slotCounts[slot] || 0) + 1;
                });

                // Filter out fully booked slots
                const available = allSlots.map(slot => ({
                    time: slot,
                    available: spaces - (slotCounts[slot] || 0),
                    isBooked: (slotCounts[slot] || 0) >= spaces
                })).filter(slot => !slot.isBooked);

                setAvailableTimeSlots(available);
            } catch (error) {
                console.error('Error loading available slots:', error);
                setMessage('Error loading available time slots');
            } finally {
                setIsLoadingSlots(false);
            }
        };

        loadAvailableSlots();
    }, [appointmentDate, doctor.id, selectedHospital.id, appointmentSettings]);

    const handleSubmit = async (event) => {
        event.preventDefault();

        // Validation
        if (!selectedHospital.id) {
            setMessage('Please select a hospital');
            return;
        }
        if (!doctor.id) {
            setMessage('Please select a doctor');
            return;
        }
        if (!patient.display_name || !patient.phone) {
            setMessage('Please enter patient name and phone');
            return;
        }
        if (!appointmentDate) {
            setMessage('Please select an appointment date');
            return;
        }
        if (!selectedTimeSlot) {
            setMessage('Please select a time slot');
            return;
        }

        setIsSubmitting(true);
        setMessage('');

        try {
            const tableSchema = '';
            const tableName = 'appointments';

            const bookingData = {
                hospital_id: selectedHospital.id,
                doctor_id: doctor.id,
                patient_id: patient.id || null,
                patient_name: patient.display_name,
                patient_phone: patient.phone,
                patient_email: patient.email || '',
                patient_age: patient.age || '',
                patient_gender: patient.gender || '',
                doctor_name: formatDoctorName(doctor.display_name),
                hospital_name: selectedHospital.display_name,
                appointment_date: appointmentDate,
                appointment_time: selectedTimeSlot,
                amount: appointmentSettings[0]?.consultant_fee || '0',
                base_amount: appointmentSettings[0]?.consultant_fee || '0',
                status: bookingStatus || 'confirmed',
                booking_date: new Date().toISOString().slice(0, 19).replace('T', ' '),
                note: bookingNotes || '',
                payment_status: 'pending',
                payment_mode: 'offline',
                created_at: new Date().toISOString().slice(0, 19).replace('T', ' ')
            };

            const conditions = {};
            const response = await oneKlicks.create(tableSchema, tableName, bookingData, conditions);

            if (response.success === true) {
                setMessage('Appointment booked successfully!');

                // Call success callback if provided
                if (onSuccess && typeof onSuccess === 'function') {
                    onSuccess(response.data);
                }

                setTimeout(() => {
                    handleClose();
                }, 1500);
            } else {
                setMessage('Failed to book appointment. Please try again.');
            }
        } catch (error) {
            console.error('Error booking appointment:', error);
            setMessage('Error booking appointment. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleLocationChange = (value) => {
        setLocation(value);
        setSelectedCoords(null);
    }

    useEffect(() => {
        if (selectedCoords !== null || !location || location.trim().length < 3) {
            setLocationSuggestions([]);
            return;
        }

        const controller = new AbortController();
        const search = async (query) => {
            try {
                setIsSearchingLocation(true);
                const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&addressdetails=1&limit=6`;
                const response = await fetch(url, {
                    headers: {
                        'Accept': 'application/json',
                        'User-Agent': 'MeddyList/1.0 (contact: support@meddylist.local)'
                    },
                    signal: controller.signal
                });
                if (!response.ok) throw new Error('Location search failed');
                const data = await response.json();
                console.log("location suggestions :", data);
                setLocationSuggestions(Array.isArray(data) ? data : []);
            } catch (err) {
                if (err.name !== 'AbortError') {
                    setLocationSuggestions([]);
                }
            } finally {
                setIsSearchingLocation(false);
            }
        };

        if (location && location.trim().length >= 3) {
            const id = setTimeout(() => search(location.trim()), 350);
            return () => {
                clearTimeout(id);
                controller.abort();
            };
        } else {
            setLocationSuggestions([]);
        }

        return () => controller.abort();
    }, [location]);

    const handlePickSuggestion = (s) => {
        setLocation(s.display_name || '');
        setSelectedCoords({ lat: parseFloat(s.lat), lon: parseFloat(s.lon) });
        setLocationSuggestions([]);
    };

    const handleHospitalChange = (value) => {
        setHospital(value);
        if (!value) {
            setHospitalList([]);
        }
    }

    useEffect(() => {
        if (selectedHospital.id || hospital.trim().length < 2) {
            setHospitalList([]);
            return;
        }
        let timeoutId;
        const fetchHospitals = async () => {
            if (!hospital || hospital.trim().length < 2) {
                setHospitalList([]);
                return;
            }
            if (!selectedCoords) {
                setHospitalList([]);
                return;
            }
            try {
                setIsSearchingHospitals(true);
                const tableSchema = '';
                const tableName = 'users';
                const conditions = {
                    user_type: 'hospitals'
                };
                const orderBy = 'id DESC';
                const search = {
                    display_name: hospital.trim(),
                };
                const pagination = {};
                const margedata = [];
                const range = {
                    latitude: selectedCoords.lat,
                    longitude: selectedCoords.lon,
                    radius: 50,
                    target_latitude: 'lat',
                    target_longitude: 'lng',
                    table_name: 'hospitals_default_location',
                    join_column: 'user_id',
                    base_column: 'id'
                };
                const response = await oneKlicks.read(tableSchema, tableName, conditions, orderBy, search, pagination, margedata);
                if (response && response.success === true) {
                    setHospitalList(Array.isArray(response.data) ? response.data : []);
                } else {
                    setHospitalList([]);
                }
            } catch (error) {
                console.error('Error getting hospitals:', error);
                setMessage('Failed to get hospitals');
                setHospitalList([]);
            } finally {
                setIsSearchingHospitals(false);
            }
        };

        timeoutId = setTimeout(fetchHospitals, 350);
        return () => clearTimeout(timeoutId);
    }, [hospital, selectedCoords]);

    const sanitizeHospitalName = (name) => {
        if (!name) return '';
        return String(name);
    };

    const handleHospitalSelect = (h) => {
        console.log("handleHospitalSelect", h);
        setSelectedHospital(h);
        setHospital(sanitizeHospitalName(h.display_name));
        setHospitalList([]);
        getDoctors();
    };

    const handleDoctorSelect = (d) => {
        setDoctor(d);
        setDoctorList([]);
        // Fetch appointment settings for the selected doctor
        fetchAppointmentSettings(d.id, selectedHospital.id);
    }

    const fetchAppointmentSettings = async (doctorId, hospitalId) => {
        if (!doctorId || !hospitalId) {
            setAppointmentSettings([]);
            return;
        }

        try {
            const tableSchema = '';
            const tableName = 'appointment_settings';
            const conditions = {
                doctor_id: doctorId,
                hospital_id: hospitalId,
                status: 'active' // Only get active settings
            };
            const orderBy = 'id DESC';
            const search = {};
            const pagination = {};
            const margedata = [];
            const response = await oneKlicks.read(tableSchema, tableName, conditions, orderBy, search, pagination, margedata);

            console.log("getAppointmentSettings response", response);

            if (response.success === true && response.data && response.data.length > 0) {
                setAppointmentSettings(response.data);
                setMessage('');
            } else {
                setAppointmentSettings([]);
                setMessage('No appointment settings found for this doctor at this hospital');
            }
        } catch (error) {
            console.error('Error fetching appointment settings:', error);
            setAppointmentSettings([]);
            setMessage('Error loading appointment settings');
        }
    }

    const getDoctors = async (name = '') => {
        const tableSchema = '';
        const tableName = 'hospital_doctors';
        const conditions = {
            hospital_id: selectedHospital.id,
            status: "active",
        };
        const orderBy = "RAND()";
        const search = {};
        const pagination = { page: 1, limit: 10 };
        const margedata = [
            {
                target_table: 'users',
                target_column: 'id',
                target_value: 'doctor_id',
                target_label: 'doctors',
                margedata: [
                    {
                        target_table: 'doctor_experience_education',
                        target_column: 'user_id',
                        target_value: 'id',
                        target_label: 'doctor_experience_education'
                    },
                    {
                        target_table: 'doctor_specialities',
                        target_column: 'doctor_id',
                        target_value: 'id',
                        target_label: 'doctor_specialities',
                        margedata: [
                            {
                                target_table: 'specialities',
                                target_column: 'id',
                                target_value: 'speciality_id',
                                target_label: 'specialities'
                            },
                            {
                                target_table: 'services',
                                target_column: 'id',
                                target_value: 'service_id',
                                target_label: 'services'
                            }
                        ]
                    }
                ]
            }
        ];
        const response = await oneKlicks.read(tableSchema, tableName, conditions, orderBy, search, pagination, margedata);


        console.log('doctors list response:', response);


        //console.log('doctors list response:', response);
        if (response.success === true) {
            console.log('doctors list response:', response);
            if (response.data && response.data.length > 0) {
                const aggregatedDoctors = response.data.flatMap(item => Array.isArray(item.doctors) ? item.doctors : []);
                if (aggregatedDoctors.length > 0) {
                    setDoctorList(aggregatedDoctors);
                } else {
                    setDoctorList([]);
                }
            } else {
                setDoctorList([]);
            }
        } else {
            setDoctorList([]);
        }
    };

    const handleDoctorChange = (value) => {
        setDoctor({});
        getDoctors(value);
    }

    const handlePatientChange = (value) => {
        setPatientSearchTerm(value);
        if (patient.id) {
            // User is editing after selecting a patient, clear selection
            setPatient({});
        }
        if (value.trim().length >= 3) {
            getPatient(value);
        } else {
            setPatientList([]);
        }
    }

    const getPatient = async (value) => {
        const tableSchema = '';
        const tableName = 'users';
        const conditions = {
            user_type: 'patient'
        };
        const orderBy = 'id DESC';
        const search = {
            phone: value.trim(),
            email: value.trim()
        };
        const pagination = { page: 1, limit: 10 };
        const margedata = [];
        const response = await oneKlicks.read(tableSchema, tableName, conditions, orderBy, search, pagination, margedata);
        if (response.success === true && response.data) {
            setPatientList(response.data);
        } else {
            setPatientList([]);
        }
    }

    const handlePatientSelect = (p) => {
        console.log("handlePatientSelect", p);
        setPatient(p);
        setPatientSearchTerm(p.phone || p.email || '');
        setPatientList([]);
    }

    return (
        <Modal show={showAdd} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>Add Appointment</Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    <Form.Group className="mb-3" controlId="hospital">
                        <Form.Label>Location</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Search location (powered by OpenStreetMap)"
                            value={location}
                            onChange={(e) => handleLocationChange(e.target.value)}
                            autoComplete="off"
                        />
                        {(isSearchingLocation || (locationSuggestions && locationSuggestions.length > 0)) && (
                            <div className="border rounded mt-2" style={{ maxHeight: 220, overflowY: 'auto' }}>
                                {isSearchingLocation && (
                                    <div className="p-2 text-muted">Searching…</div>
                                )}
                                {!isSearchingLocation && locationSuggestions.map((s) => (
                                    <button
                                        key={`${s.place_id}`}
                                        type="button"
                                        className="list-group-item list-group-item-action"
                                        onClick={() => handlePickSuggestion(s)}
                                        style={{ width: '100%', textAlign: 'left' }}
                                    >
                                        {s.display_name}
                                    </button>
                                ))}
                                {!isSearchingLocation && locationSuggestions.length === 0 && location.trim().length >= 3 && (
                                    <div className="p-2 text-muted">No results</div>
                                )}
                            </div>
                        )}
                        {selectedCoords && (
                            <div className="form-text">Selected: {selectedCoords.lat.toFixed(5)}, {selectedCoords.lon.toFixed(5)}</div>
                        )}
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="hospital">
                        <Form.Label>Hospital</Form.Label>
                        <Form.Control type="text" placeholder="Enter hospital" value={hospital} onChange={(e) => handleHospitalChange(e.target.value)} />
                        {hospitalList && hospitalList.length > 0 && (
                            <div className="border rounded mt-2" style={{ maxHeight: 240, overflowY: 'auto' }}>
                                {hospitalList.map((h) => (
                                    <button
                                        key={h.id}
                                        type="button"
                                        className="dropdown-item d-flex align-items-center"
                                        onClick={() => handleHospitalSelect(h)}
                                        style={{ width: '100%', textAlign: 'left', paddingLeft: 2 }}
                                    >
                                        <ImageShow url={h.profile_photo_url} height={40} width={40} style={{ borderRadius: '50%' }} />
                                        <div className="d-flex flex-column ms-2">
                                            <div className="hospital-name">{sanitizeHospitalName(h.display_name)}</div>
                                            <div className="hospital-address text-muted" style={{ fontSize: 12 }}>
                                                {h.address}
                                            </div>
                                        </div>
                                    </button>
                                ))}
                                {isSearchingHospitals && (
                                    <div className="p-2 text-muted">Searching hospitals…</div>
                                )}
                            </div>
                        )}

                        {selectedHospital && (
                            <div className="form-text">Selected hospital ID: {selectedHospital.id}</div>
                        )}
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="doctor">
                        <Form.Label>Doctor</Form.Label>
                        <Form.Control type="text" placeholder="Enter doctor" value={formatDoctorName(doctor.display_name)} onChange={(e) => handleDoctorChange(e.target.value)} />
                        {doctorList && doctorList.length > 0 && (
                            <div className="border rounded mt-2" style={{ maxHeight: 240, overflowY: 'auto' }}>
                                {doctorList.map((d) => (
                                    <button key={d.id} type="button" className="dropdown-item d-flex align-items-center" onClick={() => handleDoctorSelect(d)} style={{ width: '100%', textAlign: 'left', paddingLeft: 2 }}>
                                        <ImageShow url={d.profile_photo_url} height={40} width={40} style={{ borderRadius: '50%' }} />
                                        <div className="d-flex flex-column ms-2 ml-2" style={{ borderBottom: '1px solid #0000001f' }}>
                                            <div className="doctor-name" style={{ fontWeight: 'bold', fontSize: 14, marginBottom: -7 }}>{formatDoctorName(d.display_name)}</div>
                                            <div className="doctor-address text-muted" style={{ fontSize: 10 }}>
                                                {d.address}
                                            </div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </Form.Group>

                    {appointmentSettings.length > 0 && (
                        <div className="alert alert-info mb-3">
                            <strong>Consultation Fee:</strong> ₹{appointmentSettings[0]?.consultant_fee || '0'}
                        </div>
                    )}

                    <Form.Group className="mb-3" controlId="patient">
                        <Form.Label>Patient (Search by Phone or Email)</Form.Label>
                        <Form.Control type="text" placeholder="Enter patient phone or email (min 3 characters)" value={patientSearchTerm} onChange={(e) => handlePatientChange(e.target.value)} />
                        {patientList && patientList.length > 0 && (
                            <div className="border rounded mt-2" style={{ maxHeight: 240, overflowY: 'auto' }}>
                                {patientList.map((p) => (
                                    <button key={p.id} type="button" className="dropdown-item d-flex align-items-center" onClick={() => handlePatientSelect(p)} style={{ width: '100%', textAlign: 'left', paddingLeft: 2 }}>
                                        <ImageShow url={p.profile_photo_url} height={40} width={40} style={{ borderRadius: '50%' }} />
                                        <div className="d-flex flex-column ms-2 ml-2" style={{ borderBottom: '1px solid #0000001f' }}>
                                            <div className="patient-name" style={{ fontWeight: 'bold', fontSize: 14, marginBottom: -7 }}>{formatDoctorName(p.display_name)}</div>
                                            <div className="patient-address text-muted" style={{ fontSize: 10 }}>
                                                {p.address}
                                            </div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </Form.Group>
                    {patient.id && (
                        <div className="form-text">Selected patient ID: {patient.id}</div>
                    )}


                    <Form.Group className="mb-3" controlId="date">
                        <Form.Label>Name</Form.Label>
                        <Form.Control type="text" placeholder="Enter Patient Name" value={patient.display_name} onChange={(e) => setPatient({ ...patient, display_name: e.target.value })} />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="age">
                        <Form.Label>Age</Form.Label>
                        <Form.Control type="number" placeholder="Enter Patient Age" value={patient.age} onChange={(e) => setPatient({ ...patient, age: e.target.value })} />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="gender">
                        <Form.Label>Gender</Form.Label>
                        <Form.Select type="text" className="form-control" placeholder="Enter Patient Gender" value={patient.gender} onChange={(e) => setPatient({ ...patient, gender: e.target.value })}>
                            <option value="male">Male</option>
                            <option value="female">Female</option>
                            <option value="other">Other</option>
                        </Form.Select>
                    </Form.Group>

                    {/* Calendar */}
                    <div className="mb-3">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                            <div className="fw-bold">Calendar</div>
                            <div className="d-flex align-items-center" style={{ gap: 8 }}>
                                <Button variant="light" size="sm" onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1))}>{'<'}</Button>
                                <Form.Select
                                    size="sm"
                                    value={calendarMonth.getMonth()}
                                    onChange={(e) => setCalendarMonth(new Date(calendarMonth.getFullYear(), parseInt(e.target.value, 10), 1))}
                                    style={{ width: 120 }}
                                >
                                    {Array.from({ length: 12 }).map((_, i) => (
                                        <option key={i} value={i}>{new Date(2000, i, 1).toLocaleString(undefined, { month: 'long' })}</option>
                                    ))}
                                </Form.Select>
                                <Form.Select
                                    size="sm"
                                    value={calendarMonth.getFullYear()}
                                    onChange={(e) => setCalendarMonth(new Date(parseInt(e.target.value, 10), calendarMonth.getMonth(), 1))}
                                    style={{ width: 90 }}
                                >
                                    {Array.from({ length: 7 }).map((_, i) => {
                                        const year = new Date().getFullYear() - 3 + i;
                                        return <option key={year} value={year}>{year}</option>
                                    })}
                                </Form.Select>
                                <Button variant="light" size="sm" onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1))}>{'>'}</Button>
                            </div>
                        </div>
                        <div className="border rounded p-2">
                            {/* Weekday header row */}
                            <div className="d-flex mb-1" style={{ width: '100%' }}>
                                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
                                    <div key={d} className="text-muted" style={{ fontSize: 12, textAlign: 'center', width: '14.2857%' }}>{d}</div>
                                ))}
                            </div>
                            {/* 6 rows x 7 days grid */}
                            {(() => {
                                const startOfMonth = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1);
                                const startDay = startOfMonth.getDay();
                                const gridStart = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1 - startDay);
                                const today = new Date(new Date().toDateString());
                                const rows = [];
                                for (let week = 0; week < 6; week++) {
                                    const cells = [];
                                    for (let day = 0; day < 7; day++) {
                                        const dateObj = new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + (week * 7 + day));
                                        const iso = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}-${String(dateObj.getDate()).padStart(2, '0')}`;
                                        const inCurrentMonth = dateObj.getMonth() === calendarMonth.getMonth();
                                        const isSelected = appointmentDate === iso;
                                        const isPast = dateObj < today;
                                        const nowLocal = new Date();
                                        const todayIso = `${nowLocal.getFullYear()}-${String(nowLocal.getMonth() + 1).padStart(2, '0')}-${String(nowLocal.getDate()).padStart(2, '0')}`;
                                        const isToday = iso === todayIso;
                                        cells.push(
                                            <div key={`cell-${week}-${day}`} style={{ width: '14.2857%', padding: 2 }}>
                                                <button
                                                    type="button"
                                                    className={`btn btn-sm w-100 ${isSelected ? 'btn-primary' : isToday ? 'btn-outline-primary' : 'btn-outline-light'} ${inCurrentMonth ? '' : 'text-muted'}`}
                                                    onClick={() => !isPast && setAppointmentDate(iso)}
                                                    disabled={isPast}
                                                >
                                                    {dateObj.getDate()}
                                                </button>
                                            </div>
                                        );
                                    }
                                    rows.push(
                                        <div key={`week-${week}`} className="d-flex">
                                            {cells}
                                        </div>
                                    );
                                }
                                return rows;
                            })()}
                        </div>
                        <div className="mt-2">
                            <Form.Label className="mb-1">Date</Form.Label>
                            <Form.Control type="date" value={appointmentDate} onChange={(e) => setAppointmentDate(e.target.value)} min={`${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}-${String(new Date().getDate()).padStart(2, '0')}`} />
                        </div>
                    </div>

                    {/* Available Time Slots */}
                    <Form.Group className="mb-3" controlId="timeSlot">
                        <Form.Label>Available Time Slots</Form.Label>
                        {isLoadingSlots && (
                            <div className="text-center p-3">
                                <Spinner animation="border" size="sm" /> Loading available slots...
                            </div>
                        )}
                        {!isLoadingSlots && appointmentDate && doctor.id && availableTimeSlots.length === 0 && (
                            <Alert variant="warning">
                                No available time slots for the selected date. Please choose another date or check if the doctor has set up availability for this day.
                            </Alert>
                        )}
                        {!isLoadingSlots && availableTimeSlots.length > 0 && (
                            <div className="border rounded p-2" style={{ maxHeight: 200, overflowY: 'auto' }}>
                                <div className="d-flex flex-wrap gap-2">
                                    {availableTimeSlots.map((slot, index) => (
                                        <button
                                            key={index}
                                            type="button"
                                            className={`btn btn-sm ${selectedTimeSlot === slot.time ? 'btn-primary' : 'btn-outline-primary'}`}
                                            onClick={() => setSelectedTimeSlot(slot.time)}
                                            style={{ minWidth: 80 }}
                                        >
                                            {slot.time}
                                            {slot.available < 3 && slot.available > 1 && (
                                                <small className="d-block" style={{ fontSize: 9 }}>
                                                    {slot.available} left
                                                </small>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        {!appointmentDate && (
                            <div className="form-text text-muted">Please select a date first to see available time slots</div>
                        )}
                        {appointmentDate && !doctor.id && (
                            <div className="form-text text-muted">Please select a doctor to see available time slots</div>
                        )}
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="status">
                        <Form.Label>Status</Form.Label>
                        <Form.Select value={bookingStatus} onChange={(e) => setBookingStatus(e.target.value)}>
                            <option value="confirmed">Confirmed</option>
                            <option value="pending">Pending</option>
                            <option value="cancelled">Cancelled</option>
                            <option value="completed">Completed</option>
                        </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="notes">
                        <Form.Label>Notes</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            placeholder="Enter notes or special instructions"
                            value={bookingNotes}
                            onChange={(e) => setBookingNotes(e.target.value)}
                        />
                    </Form.Group>

                    {message && (
                        <Alert variant={message.includes('successfully') ? 'success' : 'danger'}>
                            {message}
                        </Alert>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>Cancel</Button>
                    <Button type="submit" variant="primary" disabled={isSubmitting}>
                        {isSubmitting ? (
                            <>
                                <Spinner animation="border" size="sm" /> Booking...
                            </>
                        ) : (
                            'Book Appointment'
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default AddAppointmentModal;
